/**
 * WCAG-compliant luminance-based text color selection.
 *
 * Given a hex background color, returns a contrasting text color:
 * dark text for light backgrounds, white for dark backgrounds.
 *
 * Used by both the frontend SSR (ThemeStyle in root.tsx) and the
 * portal canvas (EditorCanvas useTenantVars) to compute
 * --tenant-on-* CSS custom properties at runtime.
 */

/**
 * Linearize an sRGB channel value (0-255) to linear light.
 * Per the sRGB spec / WCAG 2.x relative luminance formula.
 */
function linearize(val: number): number {
  const c = val / 255;
  return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
}

/**
 * Compute WCAG 2.x relative luminance from a hex color string.
 * Returns a value between 0 (black) and 1 (white).
 * Returns -1 for unparseable input.
 */
function relativeLuminance(hex: string): number {
  const h = hex.trim().replace("#", "");
  let r: number, g: number, b: number;

  if (h.length === 6) {
    r = parseInt(h.slice(0, 2), 16);
    g = parseInt(h.slice(2, 4), 16);
    b = parseInt(h.slice(4, 6), 16);
  } else if (h.length === 3) {
    r = parseInt(h[0] + h[0], 16);
    g = parseInt(h[1] + h[1], 16);
    b = parseInt(h[2] + h[2], 16);
  } else {
    return -1;
  }

  if (isNaN(r) || isNaN(g) || isNaN(b)) return -1;

  return 0.2126 * linearize(r) + 0.7152 * linearize(g) + 0.0722 * linearize(b);
}

/**
 * Return a contrasting text color for the given hex background.
 *
 * - Light backgrounds (luminance > 0.179) get dark text (#1a1a1a).
 * - Dark backgrounds (luminance <= 0.179) get white text (#ffffff).
 * - Invalid/unparseable hex returns '#ffffff' (safe default for the
 *   common case of dark tenant backgrounds).
 */
export function onColor(hex: string): string {
  const L = relativeLuminance(hex);
  if (L < 0) return "#ffffff"; // invalid hex -- safe default
  return L > 0.179 ? "#1a1a1a" : "#ffffff";
}
